import { SITE_PROMO_CHROME_BOTTOM_CSS_VAR } from '@/lib/promotion-banner/sitePromoChromeInset'

/** Closed chat host offset from the viewport bottom while a hero slot is on screen. */
export const HERO_CHAT_LAUNCHER_BOTTOM_CSS_VAR = '--hero-chat-launcher-bottom'

/** Reserve the in-flow chat row only below Tailwind `md` (768px). */
export const HERO_CHAT_LAUNCHER_SLOT_MEDIA_QUERY = '(max-width: 767px)'

export function shouldReserveHeroChatLauncherSlot(viewportWidth: number): boolean {
  return viewportWidth < 768
}

/**
 * Pulls the H1 up under the chat row (`-mb-10`, 2.5rem).
 * The closed host subtracts the same 40px from `bottom` so it lowers without a transform jump.
 */
export const HERO_CHAT_SLOT_NEGATIVE_MARGIN_PX = 40

type ClosedChatBottomListener = () => void

let publishedClosedChatBottomPx = 0
const closedChatBottomListeners = new Set<ClosedChatBottomListener>()

export function getPublishedClosedChatBottomPx(): number {
  return publishedClosedChatBottomPx
}

export function subscribeClosedChatBottomPx(listener: ClosedChatBottomListener) {
  closedChatBottomListeners.add(listener)

  return () => {
    closedChatBottomListeners.delete(listener)
  }
}

export function readSitePromoChromeBottomPx(): number {
  if (typeof document === 'undefined') {
    return 0
  }

  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(SITE_PROMO_CHROME_BOTTOM_CSS_VAR)
    .trim()
  const parsed = Number.parseFloat(raw)

  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

export function measureHeroChatLauncherBottomInset(element: HTMLElement): number {
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) {
    return 0
  }

  return Math.max(0, Math.ceil(window.innerHeight - rect.bottom))
}

export function measureRaisedHeroChatBottomInset(placeholder: HTMLElement): number {
  const fromSlot = measureHeroChatLauncherBottomInset(placeholder)
  const chromePx = readSitePromoChromeBottomPx()
  const heading = placeholder.parentElement?.nextElementSibling
  const headingHeight =
    heading instanceof HTMLElement ? heading.getBoundingClientRect().height : 0
  const fromHeading =
    headingHeight > 0 ? Math.ceil(chromePx + headingHeight) : 0

  return Math.max(fromSlot, fromHeading)
}

export function resolveClosedChatBottomPx(raisedPx: number, promoChromePx: number): number {
  return Math.max(promoChromePx, raisedPx - HERO_CHAT_SLOT_NEGATIVE_MARGIN_PX)
}

function publishClosedChatBottomPx(px: number) {
  if (publishedClosedChatBottomPx === px) {
    return
  }

  publishedClosedChatBottomPx = px
  closedChatBottomListeners.forEach((listener) => {
    listener()
  })
}

export function setHeroChatLauncherBottomInset(px: number) {
  document.documentElement.style.setProperty(HERO_CHAT_LAUNCHER_BOTTOM_CSS_VAR, `${px}px`)
  publishClosedChatBottomPx(px)
}

export function clearHeroChatLauncherBottomInset() {
  publishClosedChatBottomPx(readSitePromoChromeBottomPx())
}
